import { useState, useCallback } from "react"
import { useQuery, useMutation, useSubscription } from "urql"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { useDebouncedValue } from "@/hooks/use-debounced-value"
import { useCursorPagination } from "@/hooks/use-cursor-pagination"
import { Plus, Trash2 } from "lucide-react"
import { toast } from "sonner"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { PageHeader } from "@/components/shared/page-header"
import { DataTable, type Column } from "@/components/shared/data-table"
import { TimeAgo } from "@/components/shared/time-ago"
import { ConfirmDialog } from "@/components/shared/confirm-dialog"
import { BulkActionBar } from "@/components/shared/bulk-action-bar"
import { SubscriptionForm } from "./components/subscription-form"
import { SourcesQuery, DestinationsQuery, SubscriptionsQuery } from "@/lib/graphql/queries"
import { DeleteSubscriptionMutation, BulkDeleteSubscriptionsMutation } from "@/lib/graphql/mutations"
import { SubscriptionChangedSubscription } from "@/lib/graphql/subscriptions"
import { useAuth } from "@/hooks/use-auth"
import type { ResultOf } from "@/lib/graphql/types"

type SubItem = ResultOf<typeof SubscriptionsQuery>["subscriptions"]["nodes"][number]

const LIMIT = 25

export function SubscriptionsPage() {
  const { isConfigured } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchInput, setSearchInput] = useState("")
  const debouncedSearch = useDebouncedValue(searchInput)

  const sourceId = searchParams.get("sourceId") ?? undefined
  const destinationId = searchParams.get("destinationId") ?? undefined
  const enabledParam = searchParams.get("enabled")
  const enabled = enabledParam === "true" ? true : enabledParam === "false" ? false : undefined
  const search = debouncedSearch || undefined
  const pagination = useCursorPagination(LIMIT, [sourceId, destinationId, enabled, search])

  const [sources] = useQuery({ query: SourcesQuery })
  const [destinations] = useQuery({ query: DestinationsQuery })
  const [result, reexecute] = useQuery({
    query: SubscriptionsQuery,
    variables: { sourceId, destinationId, enabled, search, after: pagination.after, first: pagination.first },
  })
  const handleSubChanged = useCallback(
    (
      _prev: ResultOf<typeof SubscriptionChangedSubscription> | undefined,
      response: ResultOf<typeof SubscriptionChangedSubscription>,
    ) => {
      if (response?.subscriptionChanged) {
        reexecute({ requestPolicy: "network-only" })
      }
      return response
    },
    [reexecute],
  )

  useSubscription({ query: SubscriptionChangedSubscription, pause: !isConfigured }, handleSubChanged)

  const [, deleteSub] = useMutation(DeleteSubscriptionMutation)
  const [, bulkDeleteSubs] = useMutation(BulkDeleteSubscriptionsMutation)
  const [formOpen, setFormOpen] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set())
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)

  const columns: Column<SubItem>[] = [
    {
      header: "Name",
      cell: (row) => <span className="font-medium">{row.name}</span>,
    },
    {
      header: "Source",
      cell: (row) => (
        <Link
          to={`/sources/${row.source.id}`}
          className="text-xs hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {row.source.name}
        </Link>
      ),
    },
    {
      header: "Destination",
      cell: (row) => (
        <Link
          to={`/destinations/${row.destination.id}`}
          className="text-xs hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {row.destination.name}
        </Link>
      ),
    },
    {
      header: "Filter",
      cell: (row) => {
        const parts: string[] = []
        if (row.filterConfig?.eventTypes?.length) parts.push(row.filterConfig.eventTypes.join(", "))
        if (row.filterConfig?.regex?.length) parts.push(`+${row.filterConfig.regex.length} regex`)
        if (row.filterConfig?.bodyMatch?.length) parts.push(`+${row.filterConfig.bodyMatch.length} body`)
        if (row.filterConfig?.rules?.length) parts.push(`+${row.filterConfig.rules.length} rules`)
        return (
          <span className="text-xs text-muted-foreground font-mono">
            {parts.length > 0 ? parts.join(" ") : "*"}
          </span>
        )
      },
    },
    {
      header: "Status",
      cell: (row) => (
        <Badge variant={row.enabled ? "default" : "secondary"} className="text-2xs">
          {row.enabled ? "Enabled" : "Disabled"}
        </Badge>
      ),
    },
    {
      header: "Created",
      cell: (row) => <TimeAgo date={row.createdAt} />,
    },
    {
      header: "",
      cell: (row) => (
        <Button
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
          aria-label="Delete"
          onClick={(e) => {
            e.stopPropagation()
            setDeleteId(row.id)
          }}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      ),
      className: "w-12 text-right",
    },
  ]

  const setFilter = (key: string, value: string) => {
    const params = new URLSearchParams(searchParams)
    if (value) params.set(key, value)
    else params.delete(key)
    setSearchParams(params, { replace: true })
  }

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    const res = await deleteSub({ id: deleteId })
    setDeleting(false)
    if (res.error) { toast.error(res.error.message); return }
    toast.success("Subscription deleted")
    setDeleteId(null)
    reexecute({ requestPolicy: "network-only" })
  }

  const handleBulkDelete = async () => {
    setBulkDeleting(true)
    const res = await bulkDeleteSubs({ ids: Array.from(selectedKeys) })
    setBulkDeleting(false)
    if (res.error) { toast.error(res.error.message); return }
    toast.success(`Deleted ${res.data?.bulkDeleteSubscriptions ?? 0} subscriptions`)
    setSelectedKeys(new Set())
    setBulkDeleteOpen(false)
    reexecute({ requestPolicy: "network-only" })
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Subscriptions" description="Event routing rules">
        <Button onClick={() => setFormOpen(true)} size="sm" className="shadow-primary-glow transition-shadow">
          <Plus className="h-4 w-4 mr-1.5" />
          Create Subscription
        </Button>
      </PageHeader>

      <div className="flex items-center gap-3 rounded-lg glass-card p-3">
        <Select
          value={sourceId ?? "all"}
          onValueChange={(v) => setFilter("sourceId", v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-45">
            <SelectValue placeholder="All sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            {sources.data?.sources.nodes.map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={destinationId ?? "all"}
          onValueChange={(v) => setFilter("destinationId", v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-45">
            <SelectValue placeholder="All destinations" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All destinations</SelectItem>
            {destinations.data?.destinations.nodes.map((d) => (
              <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={enabledParam ?? "all"}
          onValueChange={(v) => setFilter("enabled", v === "all" ? "" : v)}
        >
          <SelectTrigger className="w-35">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="true">Enabled</SelectItem>
            <SelectItem value="false">Disabled</SelectItem>
          </SelectContent>
        </Select>

        <Input
          placeholder="Search subscriptions..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-50 text-sm"
        />
      </div>

      <BulkActionBar count={selectedKeys.size} onClear={() => setSelectedKeys(new Set())}>
        <Button variant="destructive" size="sm" onClick={() => setBulkDeleteOpen(true)}>
          <Trash2 className="h-3.5 w-3.5 mr-1.5" />
          Delete
        </Button>
      </BulkActionBar>

      <DataTable
        columns={columns}
        data={result.data?.subscriptions.nodes ?? []}
        keyFn={(row) => row.id}
        cursorInfo={result.data?.subscriptions.pageInfo}
        cursorCallbacks={pagination}
        pageSize={LIMIT}
        loading={result.fetching}
        emptyTitle="No subscriptions"
        emptyDescription="Create a subscription to route events from sources to destinations"
        onRowClick={(row) => navigate(`/subscriptions/${row.id}`)}
        selectable
        selectedKeys={selectedKeys}
        onSelectionChange={setSelectedKeys}
      />

      <SubscriptionForm
        open={formOpen}
        onOpenChange={setFormOpen}
        onSuccess={() => reexecute({ requestPolicy: "network-only" })}
      />

      <ConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => { if (!open) setDeleteId(null) }}
        title="Delete Subscription"
        description="This will permanently delete this subscription."
        confirmLabel="Delete"
        onConfirm={handleDelete}
        loading={deleting}
        destructive
      />

      <ConfirmDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        title="Delete Subscriptions"
        description={`This will permanently delete ${selectedKeys.size} selected subscriptions.`}
        confirmLabel="Delete"
        onConfirm={handleBulkDelete}
        loading={bulkDeleting}
        destructive
      />
    </div>
  )
}
